import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  REGS_CATALOGUE,
  parseRegsList,
  serializeRegsList,
} from '../constants/riskAssessment';

/**
 * Applicable regulations picker (Chunk D).
 *
 * Props:
 *  - value: string — JSON-stringified array of catalogue slugs
 *  - onChange?(nextJsonString): local-state echo (every click)
 *  - onCommit?(nextJsonString): persist (every click — toggles are atomic)
 *  - disabled: bool
 *  - readOnly: bool — when true, only the selected chips render (no toggle
 *      affordance). Used in the library viewer.
 *
 * Editor mode: 14 chips, all visible at rest. Selected = filled indigo;
 * unselected = ghost. Click to toggle.
 *
 * Read-only mode: only selected chips render, plain pill style.
 */
export default function RegsSelector({
  value,
  onChange,
  onCommit,
  disabled = false,
  readOnly = false,
}) {
  const initialSet = useMemo(() => new Set(parseRegsList(value)), [value]);
  const [selected, setSelected] = useState(initialSet);

  useEffect(() => {
    setSelected(new Set(parseRegsList(value)));
  }, [value]);

  const toggle = useCallback(
    (slug) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(slug)) next.delete(slug);
        else next.add(slug);
        const arr = [...next];
        const str = serializeRegsList(arr);
        onChange?.(str);
        onCommit?.(str);
        return next;
      });
    },
    [onChange, onCommit]
  );

  if (readOnly) {
    const items = REGS_CATALOGUE.filter((r) => selected.has(r.slug));
    if (items.length === 0) {
      return (
        <div className="mt-1 text-[11px] italic text-gray-400 dark:text-gray-500">
          No regs specified.
        </div>
      );
    }
    return (
      <div className="mt-1 flex flex-wrap gap-1">
        {items.map((r) => (
          <span
            key={r.slug}
            title={r.long}
            className="inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200"
          >
            {r.short}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {REGS_CATALOGUE.map((r) => {
        const isOn = selected.has(r.slug);
        return (
          <button
            key={r.slug}
            type="button"
            disabled={disabled}
            onClick={() => toggle(r.slug)}
            title={r.long}
            aria-pressed={isOn}
            className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded transition-all ${
              isOn
                ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200 ring-1 ring-indigo-400 dark:ring-indigo-500'
                : 'bg-transparent text-gray-500 dark:text-gray-400 ring-1 ring-gray-300 dark:ring-gray-600 hover:ring-gray-400 dark:hover:ring-gray-500'
            } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          >
            {r.short}
          </button>
        );
      })}
    </div>
  );
}
